import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";

import { FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import { useParams, useNavigate } from 'react-router-dom';
import {
  getLead,
  updateLead,
  initiateCall,
  endCall,
  logFollowUp,
  createTask,
  completeTask,
  createLeadComment,
  getLeadComments,
  getUsers,
  getLeadTimeline,
  dropLead,
  getDropReasons,
getLeadStages,
getLeadQualifyingQuestions,
saveLeadQualifyingAnswer
} from "../api";
import { useAuth } from '../context/AuthContext';
import AddDealModal from "./AddDealModal";


function fmtSecs(s) { const m=Math.floor(s/60); return `${m}m ${s%60}s`; }
function fmtDate(dateString) {
  if (!dateString) return "—";

  const date = new Date(dateString);

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [callId, setCallId] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const [callSecs, setCallSecs] = useState(0);
  const [callNote, setCallNote] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const [taskTime, setTaskTime] = useState('');
  const [toast, setToast] = useState('');
  const [showDropModal, setShowDropModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [dropReason, setDropReason] = useState("");
  const [dropReasons, setDropReasons] = useState([]);
  const [dropComment, setDropComment] = useState("");
  const [leadStages, setLeadStages] = useState([]);
  const [qualifyingQuestions, setQualifyingQuestions] = useState([]);
  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [leadNote, setLeadNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editData, setEditData] = useState({name: "",phone: "",});
  const timerRef = useRef(null);

const [taskAddedThisSession, setTaskAddedThisSession] = useState(false);
const [commentAddedThisSession, setCommentAddedThisSession] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    try {
      const [
  leadRes,
  usersRes,
  historyRes,
  commentsRes,
  reasonsRes,
  stagesRes,
  qualifyingRes
] = await Promise.all([
  getLead(id),
  getUsers(),
  getLeadTimeline(id),
  getLeadComments(id),
  getDropReasons(),
  getLeadStages(),
  getLeadQualifyingQuestions(id)
]);

setLead(leadRes.data);
setLeadNote(leadRes.data.user_notes || "");

setEditData({
  name: leadRes.data.name || "",
  phone: leadRes.data.phone || "",
});
setUsers(usersRes.data);
setHistory(historyRes.data);
setComments(commentsRes.data);
setDropReasons(reasonsRes.data);
setLeadStages(stagesRes.data);
setQualifyingQuestions(qualifyingRes.data);

} catch {
    navigate("/leads");
} finally {
    setLoading(false);
}
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const update = async (field, value) => {
    setSaving(true);
    try {
      await updateLead(id, { [field]: value });
      setLead(prev => ({ ...prev, [field]: value }));
      showToast('Saved');
      if (field === 'stage') load(); // reload timeline
    } catch { showToast('Save failed'); }
    finally { setSaving(false); }
  };

  const saveLeadField = async (field) => {
  try {
    await updateLead(id, {
      [field]: editData[field],
    });

    setLead((prev) => ({
      ...prev,
      [field]: editData[field],
    }));

    setEditingField(null);

    showToast("Updated Successfully");
  } catch (err) {
    showToast("Update Failed");
  }
};

  const startCall = async () => {
    try {
      const res = await initiateCall(parseInt(id), '');
      setCallId(res.data.call_id);
      setCallActive(true);
      setCallSecs(0);
      timerRef.current = setInterval(() => setCallSecs(s => s + 1), 1000);
    } catch { showToast('Could not start call'); }
  };

  const stopCall = async () => {
    clearInterval(timerRef.current);
    const dur = callSecs;
    setCallActive(false);
    const note = window.prompt('Add a call note (optional):', 'Follow-up call') || 'Call logged';
    try {
      await endCall(callId, dur, note);
      showToast(`Call saved — ${fmtSecs(dur)}`);
      load();
    } catch { showToast('Call save failed'); }
    setCallSecs(0); setCallId(null);
  };

  const addFollowUp = async () => {
    const desc = window.prompt('Follow-up note:') || 'Follow-up logged';
    try {
      await logFollowUp(id, desc);
      showToast('Follow-up logged');
      load();
    } catch { showToast('Failed'); }
  };

  

  const doneTask = async (taskId) => {
    try { await completeTask(taskId); load(); } catch { showToast('Failed'); }
  };

  const addComment = async () => {
  if (!newComment.trim()) {
    showToast("Please enter a comment");
    return;
  }

  if (!taskDue) {
    showToast("Please select follow-up date");
    return;
  }

  if (!taskTime) {
    showToast("Please select follow-up time");
    return;
  }

  try {
    // ==========================================
    // 1. SAVE COMMENT
    // ==========================================
    await createLeadComment(id, {
      comment: newComment.trim(),
    });

    // ==========================================
    // 2. CREATE TASK / FOLLOW-UP
    // Same comment becomes task title
    // ==========================================
    await createTask({
      lead_id: Number(id),
      title: newComment.trim(),
      due_date: taskDue,
      due_time: taskTime,
    });

    // ==========================================
    // CLEAR FORM
    // ==========================================
    setNewComment("");
    setTaskDue("");
    setTaskTime("");

    // Both completed in one action
    setCommentAddedThisSession(true);
    setTaskAddedThisSession(true);

    showToast("Comment and follow-up added successfully");

    await load();

  } catch (err) {
    console.error("COMMENT / FOLLOW-UP ERROR:", err);
    console.error(
      "COMMENT / FOLLOW-UP RESPONSE:",
      err.response?.data
    );

    showToast(
      err.response?.data?.error ||
      "Could not add comment and follow-up"
    );
  }
};


  const saveLeadNote = async () => {
  try {
    setSavingNote(true);

    const response = await updateLead(id, {
      user_notes: leadNote
    });

    setLead((prev) => ({
      ...prev,
      user_notes: response.data.user_notes
    }));

    setLeadNote(response.data.user_notes || "");

    showToast("Note Saved");
  } catch (err) {
    console.error("SAVE LEAD NOTE ERROR:", err);
    console.error("SAVE LEAD NOTE RESPONSE:", err.response?.data);

    showToast(
      err.response?.data?.error ||
      "Could not save note"
    );
  } finally {
    setSavingNote(false);
  }
};

  const handleDropLead = async () => {
  if (!dropReason) {
    alert("Please select a drop reason.");
    return;
  }

  if (!dropComment.trim()) {
    alert("Please enter a drop comment.");
    return;
  }

  if (dropComment.trim().length < 5) {
    alert("Drop comment must be at least 5 characters.");
    return;
  }

  try {
    await dropLead(
      id,
      dropReason,
      dropComment.trim()
    );

    showToast("Lead Dropped");

    setDropReason("");
    setDropComment("");
    setShowDropModal(false);

    navigate("/leads");
  } catch (err) {
    console.error(err);

    alert(
      err.response?.data?.error ||
      "Could not drop lead"
    );
  }
};

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!lead) return null;

  const totalCallSecs = (lead.calls || []).reduce((s, c) => s + (c.duration_seconds || 0), 0);
  const canAssign = user?.role !== 'sales_manager';

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {toast && <div className="toast-container"><div className="toast success">{toast}</div></div>}

      {/* Header */}
      <div className="page-header">
        <div>
          <button
  className="btn btn-ghost btn-sm"
  onClick={() => {
  if (!taskAddedThisSession && !commentAddedThisSession) {
    alert(
      "Please add both a task and a comment before leaving this lead."
    );
    return;
  }

  if (!taskAddedThisSession) {
    alert(
      "Please add a follow-up task before leaving this lead."
    );
    return;
  }

  if (!commentAddedThisSession) {
    alert(
      "Please add a comment before leaving this lead."
    );
    return;
  }

  navigate(-1);
}}
  style={{ marginBottom: "4px" }}
>
  ← Back
</button> 
          <div
  className="page-title"
  style={{ display: "flex", alignItems: "center", gap: "8px" }}
>
  {editingField === "name" ? (
    <>
      <input
        className="form-input"
        value={editData.name}
        onChange={(e) =>
          setEditData({ ...editData, name: e.target.value })
        }
        style={{ width: "250px" }}
      />

      <FaCheck
        style={{ color: "green", cursor: "pointer" }}
        onClick={() => saveLeadField("name")}
      />

      <FaTimes
        style={{ color: "red", cursor: "pointer" }}
        onClick={() => setEditingField(null)}
      />
    </>
  ) : (
    <>
      {lead.name}

      <FaEdit
        style={{ cursor: "pointer", color: "#666" }}
        onClick={() => setEditingField("name")}
      />
    </>
  )}
</div>
          <div
  className="page-subtitle"
  style={{ display: "flex", alignItems: "center", gap: "8px" }}
>
  {editingField === "phone" ? (
    <>
      <input
        className="form-input"
        value={editData.phone}
        onChange={(e) =>
          setEditData({ ...editData, phone: e.target.value })
        }
        style={{ width: "180px" }}
      />

      <FaCheck
        style={{ color: "green", cursor: "pointer" }}
        onClick={() => saveLeadField("phone")}
      />

      <FaTimes
        style={{ color: "red", cursor: "pointer" }}
        onClick={() => setEditingField(null)}
      />
    </>
  ) : (
    <>
      {lead.phone}

      <FaEdit
        style={{ cursor: "pointer", color: "#666" }}
        onClick={() => setEditingField("phone")}
      />

      · {lead.city} · {lead.source}
      {lead.campaign_name ? ` · ${lead.campaign_name}` : ""}
    </>
  )}
</div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>

  <button
    className="btn btn-danger btn-sm"
    onClick={() => setShowDropModal(true)}
  >
    Drop Lead
  </button>

  

  {saving && (
    <span
      style={{
        fontSize: "12px",
        color: "#888",
        alignSelf: "center"
      }}
    >
      Saving...
    </span>
  )}

</div>
      </div>

      <div style={{ flex:1, overflow:'auto', padding:'16px 20px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:'16px', maxWidth:'1100px' }}>

          {/* Left column */}
          <div>
            {/* Pipeline controls */}
            <div className="card" style={{ marginBottom:'14px' }}>
              <div className="panel-section-title" style={{ marginBottom:'12px' }}>Pipeline status</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
                <div className="form-field" style={{ margin:0 }}>
                  <label className="form-label">Stage</label>
                  <select className="form-input" value={lead.stage} onChange={e => update('stage', e.target.value)}>
                    {leadStages.map((stage) => (
  <option
    key={stage.id}
    value={stage.stored_value || stage.stage_name}
  >
    {stage.display_name}
  </option>
))}
                  </select>
                </div>
                <div className="form-field" style={{ margin:0 }}>
                  <label className="form-label">Priority</label>
                  <select className="form-input" value={lead.priority} onChange={e => update('priority', e.target.value)}>
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cold">Cold</option>
                  </select>
                </div>
                {canAssign && (
                  <div className="form-field" style={{ margin:0 }}>
                    <label className="form-label">Assigned to</label>
                    <select className="form-input" value={lead.assigned_to || ''} onChange={e => update('assigned_to', e.target.value)}>
                      {lead.assigned_to &&
  !users.some((u) => String(u.id) === String(lead.assigned_to)) && (
    <option value={lead.assigned_to}>
      {lead.assigned_to_name || "Current user"}
    </option>
  )}

{users
  .filter((u) => {
    if (!u.is_active) return false;

    if (user?.role === "team_lead") {
      return (
        u.role === "sales_manager" &&
        String(u.team_lead_id) === String(user.id)
      ) || String(u.id) === String(lead.assigned_to);
    }

    return (
      u.role === "sales_manager" ||
      u.role === "team_lead"
    );
  })
  .map((u) => (
    <option key={u.id} value={u.id}>
      {u.name}
      {u.role === "team_lead" ? " (TL)" : ""}
    </option>
  ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            {/* Qualifying Questions */}
<div className="card" style={{ marginBottom: "14px" }}>
  <div
    className="panel-section-title"
    style={{ marginBottom: "14px" }}
  >
    Qualifying Questions
  </div>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: "14px",
    }}
  >
    {qualifyingQuestions.map((question) => (
      <div key={question.id}>
        <label
          className="form-label"
          style={{ marginBottom: "6px" }}
        >
          {question.question_text}
        </label>

        <select
          className="form-input"
          value={question.selected_option_id || ""}
          disabled={savingQuestionId === question.id}
          onChange={async (e) => {
            const optionId = Number(e.target.value);

            setSavingQuestionId(question.id);

            try {
              await saveLeadQualifyingAnswer(
                id,
                question.id,
                optionId
              );

              setQualifyingQuestions((prev) =>
                prev.map((q) =>
                  q.id === question.id
                    ? {
                        ...q,
                        selected_option_id: optionId,
                      }
                    : q
                )
              );

              showToast("Answer Saved");
            } catch (err) {
              showToast("Save Failed");
            } finally {
              setSavingQuestionId(null);
            }
          }}
        >
          <option value="">Select</option>

          {question.options.map((option) => (
            <option
              key={option.id}
              value={option.id}
            >
              {option.option_text}
            </option>
          ))}
        </select>
      </div>
    ))}
  </div>
</div>

            {/* Call box */}
            <div className="card" style={{ marginBottom:'14px' }}>
              <div className="panel-section-title" style={{ marginBottom:'10px' }}>
                Calls — total {fmtSecs(totalCallSecs)} across {lead.calls?.length || 0} call{lead.calls?.length !== 1 ? 's' : ''}
              </div>
              <div className="call-box">
                <div className="call-number">
                  <div className="call-number-label">Calling</div>
                  <div className="call-number-value">{lead.phone}</div>
                </div>
                {callActive && (
                  <div className="call-timer-display">{fmtSecs(callSecs)}</div>
                )}
                <button
                  className={`btn ${callActive ? 'btn-danger' : 'btn-primary'}`}
                  onClick={callActive ? stopCall : startCall}
                >
                  {callActive ? '⏹ End call' : '📞 Start call'}
                </button>
              </div>
              {(lead.calls || []).length > 0 && (
                <div>
                  {lead.calls.map(c => (
                    <div className="call-log-item" key={c.id}>
                      <span className="call-log-dur">{fmtSecs(c.duration_seconds)}</span>
                      <div style={{ flex:1 }}>
                        <div className="call-log-note">{c.note || 'Call logged'}</div>
                        {c.recording_url && (
                          <a href={c.recording_url} className="recording-link" target="_blank" rel="noreferrer">
                            ▶ Play recording
                          </a>
                        )}
                      </div>
                      <span className="call-log-date">{fmtDate(c.called_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tasks */}

<div className="card">
  <div
    className="panel-section-title"
    style={{ marginBottom: "10px" }}
  >
    Tasks
  </div>
              {(lead.tasks || []).length === 0 && <div style={{ fontSize:'13px', color:'#aaa' }}>No tasks yet</div>}
              {(lead.tasks || []).map(t => (
                <div key={t.id} style={{ display:'flex', gap:'10px', padding:'8px 0', borderBottom:'0.5px solid rgba(0,0,0,0.06)', alignItems:'flex-start' }}>
                  <button
                    onClick={() => !t.is_done && doneTask(t.id)}
                    style={{ width:'18px', height:'18px', borderRadius:'4px', border:'1.5px solid', borderColor: t.is_done ? '#1D9E75' : '#ccc', background: t.is_done ? '#1D9E75' : 'transparent', color:'#fff', cursor: t.is_done ? 'default' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', flexShrink:0, marginTop:'2px' }}
                  >
                    {t.is_done ? '✓' : ''}
                  </button>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', textDecoration: t.is_done ? 'line-through' : 'none', color: t.is_done ? '#aaa' : 'inherit' }}>{t.title}</div>
                    {t.due_date && <div style={{ fontSize:'10px', color:'#aaa', marginTop:'2px' }}>Due:
{new Date(t.due_date).toLocaleDateString("en-IN")}

{t.due_time ? ` ${t.due_time}` : ""}</div>}
                  </div>
                </div>
              ))}
            </div>
            {/* Comments */}

{/* Activity Timeline with Comment Box */}
<div
  className="card"
  style={{
    marginTop: "14px",
    marginBottom: "14px",
  }}
>
  <div
    className="panel-section-title"
    style={{ marginBottom: "12px" }}
  >
    Activity Timeline
  </div>

 {/* Comment + Follow-up Box */}
<div
  style={{
    display: "flex",
    gap: "8px",
    marginBottom: "18px",
    alignItems: "center",
  }}
>
  <input
    className="form-input"
    placeholder="Write a comment..."
    value={newComment}
    onChange={(e) => setNewComment(e.target.value)}
    style={{ flex: 1 }}
    onKeyDown={(e) => {
      if (e.key === "Enter") {
        addComment();
      }
    }}
  />

  <input
    className="form-input"
    type="date"
    value={taskDue}
    onChange={(e) => setTaskDue(e.target.value)}
    style={{ width: "140px" }}
  />

  <input
    className="form-input"
    type="time"
    value={taskTime}
    onChange={(e) => setTaskTime(e.target.value)}
    style={{ width: "120px" }}
  />

  <button
    className="btn btn-primary btn-sm"
    onClick={addComment}
  >
    Post
  </button>
</div>

  {/* Timeline */}
  {history.length === 0 && (
    <div
      style={{
        fontSize: "13px",
        color: "#aaa",
      }}
    >
      No activity yet
    </div>
  )}

  {history.map((h) => (
    <div
      className="timeline-item"
      key={`${h.event_type}-${h.id}`}
      style={{
        padding: "12px 0",
        borderBottom: "1px solid #eee",
      }}
    >
      <div
        className="timeline-dot"
        style={{
          background:
            h.event_type === "assignment"
              ? "#378ADD"
              : h.event_type === "stage_change"
              ? "#1D9E75"
              : h.event_type === "comment"
              ? "#BA7517"
              : h.event_type === "lead_history"
              ? "#E24B4A"
              : "#888",
        }}
      />

      <div style={{ flex: 1 }}>
        <div
          className="timeline-text"
          style={{
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {h.description}
        </div>

        <div
          className="timeline-time"
          style={{
            marginTop: "4px",
          }}
        >
          {h.user_name && `${h.user_name} · `}
          {fmtDate(h.created_at)}
        </div>
      </div>
    </div>
  ))}
</div>
            
          </div>
          
          {/* Right column */}
          <div>
            {/* Lead info */}
            <div className="card" style={{ marginBottom:'14px' }}>
              <div className="panel-section-title" style={{ marginBottom:'10px' }}>Lead details</div>
              <div className="info-grid">
                {[
                  { label:'Email', value: lead.email || '—' },
                  { label:'City', value: lead.city || '—' },
                  { label:'Product', value: lead.product_interest || '—' },
                  { label:'Budget', value: lead.budget_cr ? `₹${lead.budget_cr}Cr` : '—', green: true },
                  { label:'Source', value: lead.source },
                  { label:'Campaign', value: lead.campaign_name || '—' },
                  { label:'Source ID', value: lead.source_lead_id || '—', mono: true },
                  { label:'Follow-ups', value: lead.follow_up_count || 0 },
                  { label:'Last contacted', value: fmtDate(lead.last_contacted_at) },
                  { label:'Added', value: fmtDate(lead.created_at) },
                ].map(item => (
                  <div key={item.label}>
                    <div className="info-item-label">{item.label}</div>
                    <div style={{ fontSize:'12px', color: item.green ? '#1D9E75' : 'inherit', fontWeight: item.green ? 600 : 400, fontFamily: item.mono ? 'monospace' : 'inherit' }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              {lead.notes && (
                <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:'0.5px solid rgba(0,0,0,0.06)' }}>
                  <div className="info-item-label">Question</div>
                  <div style={{ fontSize:'12px', marginTop:'3px', lineHeight:'1.5' }}>{lead.notes}</div>
                </div>
              )}
            </div>

            {/* Assigned To */}
<div className="card" style={{ marginBottom: "14px" }}>
  <div className="panel-section-title" style={{ marginBottom: "10px" }}>
    Assigned To
  </div>

  <select
  className="form-input"
  value={lead.assigned_to || ""}
  onChange={(e) => update("assigned_to", e.target.value)}
  disabled={!["admin", "sales_head"].includes(user?.role)}
  style={{
  backgroundColor: !["admin", "sales_head"].includes(user?.role)
    ? "#f5f5f5"
    : "",
  cursor: !["admin", "sales_head"].includes(user?.role)
    ? "not-allowed"
    : "pointer"
}}

>
        {users
      .filter(
        (u) =>
          u.is_active &&
          (u.role === "sales_manager" ||
            u.role === "team_lead")
      )
      .map((u) => (
        <option key={u.id} value={u.id}>
          {u.name}
          {u.role === "team_lead" ? " (TL)" : ""}
        </option>
      ))}
  </select>
</div>

{/* note */}
<div
  className="card"
  style={{
    marginBottom: "14px",
    background: "#f5f6f8"
  }}
>

  <div
    className="panel-section-title"
    style={{ marginBottom: "10px" }}
  >
    Lead Notes
  </div>

  <textarea
    className="form-input"
    rows={6}
    placeholder="Write anything about this lead..."
    value={leadNote}
    onChange={(e) => setLeadNote(e.target.value)}
    style={{
      resize: "vertical",
      minHeight: "140px"
    }}
  />

  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      marginTop: "12px"
    }}
  >
    <button
      className="btn btn-primary btn-sm"
      onClick={saveLeadNote}
      disabled={savingNote}
    >
      {savingNote ? "Saving..." : "Save Note"}
    </button>
  </div>

</div>


{/* Add New Deal */}
<div
  className="card"
  onClick={() => setShowDealModal(true)}
  style={{
    textAlign:"center",
    padding:"30px",
    cursor:"pointer",
    marginBottom:"14px"
  }}
>
  <div
    style={{
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      border: "2px solid #1D9E75",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "34px",
      color: "#1D9E75",
      margin: "0 auto 12px"
    }}
  >
    +
  </div>

  <div
    style={{
      fontWeight: 600,
      color: "#444"
    }}
  >
    Add New Deal
  </div>
</div>
{showDropModal && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999
    }}
  >
    
    <div
      style={{
        background: "#fff",
        padding: "24px",
        borderRadius: "12px",
        width: "380px"
      }}
    >
      <h3>Drop Lead</h3>

      <select
        className="form-input"
        value={dropReason}
        onChange={(e) => setDropReason(e.target.value)}
      >
        <option value="">Select Reason</option>

{dropReasons.map((reason) => (

<option
key={reason.id}
value={reason.reason}
>

{reason.reason}

</option>

))}
      </select>

      <div
  style={{
    marginTop: "16px",
  }}
>
  <label
    style={{
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      marginBottom: "6px",
    }}
  >
    Drop Comment *
  </label>

  <textarea
    className="form-input"
    rows={4}
    placeholder="Enter reason/details for dropping this lead..."
    value={dropComment}
    onChange={(e) =>
      setDropComment(e.target.value)
    }
    maxLength={1000}
    style={{
      width: "100%",
      resize: "vertical",
      minHeight: "90px",
    }}
  />

  <div
    style={{
      marginTop: "4px",
      fontSize: "11px",
      color:
        dropComment.trim().length > 0 &&
        dropComment.trim().length < 5
          ? "#DC2626"
          : "#777",
      display: "flex",
      justifyContent: "space-between",
    }}
  >
    <span>Minimum 5 characters</span>

    <span>
      {dropComment.length}/1000
    </span>
  </div>
</div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "20px"
        }}
      >
        <button
          className="btn"
          onClick={() => setShowDropModal(false)}
        >
          Cancel
        </button>

        <button
          className="btn btn-danger"
          onClick={handleDropLead}
        >
          Drop Lead
        </button>
      </div>
    </div>
  </div>
)}
{showDealModal && (
    <AddDealModal
        lead={lead}
        users={users}
        onClose={() => setShowDealModal(false)}
        onSuccess={() => {
            setShowDealModal(false);
            showToast("Deal Created Successfully");
        }}
    />
)}

            </div>
          </div>
        </div>
      </div>
    
  );
}
